import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Lightweight, touch-reliable modal. Uses a plain portal + native buttons
// (no focus-trap / body pointer-events manipulation), so action buttons always
// respond to taps on mobile — unlike some Radix layer edge cases.
export const ConfirmModal = ({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  danger = false,
  confirmTestId = 'confirm-modal-confirm',
  cancelTestId = 'confirm-modal-cancel',
  busy = false,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 ss-fade-in"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
      data-testid="confirm-modal-overlay"
    >
      <div
        className="w-full max-w-md rounded-2xl p-5 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1 -m-1 rounded-lg" data-testid="confirm-modal-close">
            <X size={22} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
        {description && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{description}</p>}
        {children && <div className="mt-3">{children}</div>}
        {(onConfirm || confirmLabel) && (
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              data-testid={cancelTestId}
              className="rounded-full font-semibold min-h-[48px] px-5 border w-full sm:w-auto transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid={confirmTestId}
              className="rounded-full font-bold min-h-[48px] px-5 w-full sm:w-auto transition-colors disabled:opacity-60"
              style={{ background: danger ? 'var(--danger)' : 'var(--primary)', color: danger ? 'var(--danger-foreground)' : 'var(--primary-foreground)' }}
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
